/**
 * @file DesktopKeepControl.tsx
 * @description "Keep on my computer" — the one surface that creates and removes
 * a desktop sync pin. Consumers: `FormControl`'s right-hand action run and
 * `RecordDetailPanel`'s toolbar, each passing the artifact path its route names.
 *
 * **Read-only access does not disable this control.** A pin lives on the
 * reader's own user document, and nothing can pre-compute whether a later push
 * will be accepted, so refusing to pin because the artifact is read-only would
 * withhold a working action. That is a deliberate deviation from the attachments
 * panel, which does disable on read-only.
 *
 * It is disabled instead when there is nothing to pin — no signed-in user, an
 * artifact path `resolveDesktopPinPath` cannot fold, or an unloaded user
 * document — and while a write is in flight, because `keep` and `stopKeeping`
 * serialize internally and a second click would queue behind the first. Disabled
 * rather than hidden, so the toolbar does not reflow over a placeholder.
 *
 * `resolveDesktopKeepControl` owns that matrix, tooltip included, so each
 * disabled cause names itself instead of showing a pin-state sentence that does
 * not apply. Both buttons carry it and both are wrapped in a span, because antd
 * fires no tooltip off a disabled button, which is the state the sentence is
 * for. The two menu items carry their own refusals as `title`, since a menu item
 * has no room for a tooltip of its own.
 *
 * Two states today: kept and not kept. The third — kept, with no agent running —
 * arrives with the heartbeat and is not scaffolded here. "Reveal folder" is
 * omitted rather than disabled: no browser-to-agent channel outlives the auth
 * handshake and the heartbeat carries no folder path, so it would work in no
 * state at all.
 */

import { FC, useContext, useState } from 'react';
import { Button, Dropdown, Space, Tooltip, type MenuProps } from 'antd';

import { keep } from '@/use/desktopSync/keep';
import { resolveDesktopPinPath } from '@/use/desktopSync/resolveDesktopPinPath';
import { stopKeeping } from '@/use/desktopSync/stopKeeping';
import { useDesktopPin } from '@/use/desktopSync/useDesktopPin';
import { MessageContext } from '../../providers/MessageContext';
import { UserContext } from '../../providers/UserContext';
import Icon from '../atoms/Icon';
import DesktopKeepConfirm from './DesktopKeepConfirm';
import { resolveDesktopKeepControl } from './resolveDesktopKeepControl';

export type DesktopKeepControlProps = {
    /** The route's artifact path. Folded through `resolveDesktopPinPath` here; `undefined` renders the control disabled. */
    artifactPath: string | undefined;
    /** Which word the copy uses. The host knows it even over a placeholder, where the path does not exist yet. */
    artifactLabel: 'form' | 'record';
};

const DesktopKeepControl: FC<DesktopKeepControlProps> = ({ artifactPath, artifactLabel }) => {
    const { user } = useContext(UserContext);
    const { handleFirestoreWriteError } = useContext(MessageContext);
    const userId = user?.uid;
    const pinPath = resolveDesktopPinPath(artifactPath);
    const { isPinned, scope, loading } = useDesktopPin(pinPath);

    const [pending, setPending] = useState(false);
    const [confirmMode, setConfirmMode] = useState<'keep' | 'stop'>('keep');
    const [confirmOpen, setConfirmOpen] = useState(false);

    const state = resolveDesktopKeepControl({
        artifactLabel,
        pinPath,
        signedIn: userId !== undefined,
        loading,
        pending,
        isPinned,
        scope,
    });

    const write = async (run: (userId: string, pinPath: string) => Promise<void>, failure: string) => {
        if (!userId || !pinPath) return;
        setPending(true);
        try {
            await run(userId, pinPath);
            setConfirmOpen(false);
        } catch (error) {
            handleFirestoreWriteError(error, '[DesktopKeepControl] pin write', {
                userMessage: failure,
            });
        } finally {
            setPending(false);
        }
    };

    const keepWholeDocument = () => write(
        (uid, path) => keep(uid, path, { kind: 'document' }),
        `Could not keep this ${artifactLabel} on your computer.`,
    );

    const stopKeepingArtifact = () => write(
        (uid, path) => stopKeeping(uid, path),
        `Could not stop keeping this ${artifactLabel}.`,
    );

    const openConfirm = (mode: 'keep' | 'stop') => {
        setConfirmMode(mode);
        setConfirmOpen(true);
    };

    const items: MenuProps['items'] = [
        {
            key: 'keep-every-file',
            label: 'Keep every file',
            title: state.keepEveryFileDisabledReason,
            disabled: state.keepEveryFileDisabledReason !== undefined,
            onClick: () => (isPinned ? void keepWholeDocument() : openConfirm('keep')),
        },
        {
            key: 'stop-keeping',
            label: 'Stop keeping',
            danger: true,
            title: state.stopKeepingDisabledReason,
            disabled: state.stopKeepingDisabledReason !== undefined,
            onClick: () => openConfirm('stop'),
        },
    ];

    return (
        <>
            <Space.Compact>
                <Tooltip title={state.tooltip}>
                    <span style={{ display: 'inline-flex' }}>
                        <Button
                            type={isPinned ? 'primary' : 'default'}
                            icon={<Icon.HardDrive size={16} />}
                            disabled={state.disabled}
                            loading={pending}
                            onClick={() => openConfirm(isPinned ? 'stop' : 'keep')}
                            aria-label={state.ariaLabel}
                        >
                            {state.label}
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title={state.tooltip}>
                    <span style={{ display: 'inline-flex' }}>
                        <Dropdown menu={{ items }} trigger={['click']} disabled={state.disabled}>
                            <Button
                                type={isPinned ? 'primary' : 'default'}
                                icon={<Icon.ChevronDown size={16} />}
                                disabled={state.disabled}
                                aria-label={`Desktop sync options for this ${artifactLabel}`}
                            />
                        </Dropdown>
                    </span>
                </Tooltip>
            </Space.Compact>
            <DesktopKeepConfirm
                mode={confirmMode}
                open={confirmOpen}
                artifactLabel={artifactLabel}
                confirming={pending}
                onConfirm={() => void (confirmMode === 'keep' ? keepWholeDocument() : stopKeepingArtifact())}
                onCancel={() => setConfirmOpen(false)}
            />
        </>
    );
};

export default DesktopKeepControl;
